import { PUSH_SETTINGS_TYPE, SETTING_TABS, SUBSCRIPTION_MAP, useUserManagementHooks } from '@/manager/manager/agent-dashboard-user-management-manager';
import { UserDetailHeader } from './user-detail-header'
import { BasicInformation } from './basic-information'
import { ActorsInformation } from './actors-information'
import { useState, useEffect, useRef, useMemo } from 'react'
import { PersonalAgentInfo } from './personal-agent-info';
import { SubscriptionSetting } from './subscription-setting';
import { useTranslation } from 'react-i18next';
import { SettingsTabs } from './settings-tabs';
import { TemplateVideoNew } from './template-video-new';
import { TemplateImage } from './template-image';
import { MaterialLibrary } from './material-library';
import { PushSettings } from './push-settings';
import { Voice } from './voice';
import { KnowledgeBase } from './knowledge-base';
import { LoadingOverlay } from '../common/loading-overlay';
import { TemplateArticle } from './template-article';
import { cn } from '@/lib/utils';
import { ArticleBottomPoster } from './article-bottom-poster';
import { ArticleHeader } from './article-header';

/** 用户详情 */
export const AgentDashboardUserDetail = () => {
  const { t } = useTranslation();

  // 用户信息的hook
  const {
    loading,
    userBasicInfo,
    userAgentInfo,
    subscribeInfo,
    agentUserActors,
    agentUserVoices,

    // nameError,
    genderError,
    userAgentNameError,
    personalTitleError,

    changeBasicInfoValue,
    changeAgentInfoValue,
    changeSubscribeInfoValue,
    setInfoTemplateToIntroduction,

    updateUser,
    validateUsers,
    updateLoading,
    resetUserDetail,
    clearUserDetail,
  } = useUserManagementHooks.useAgentV2UsersDetail();

  // 播客音频数据Hook
  const {
    loading: podcastLoading,
    podcastVoices,
  } = useUserManagementHooks.usePodcastVoices();

  // 推送设置Hook
  const {
    loading: pushSettingsLoading,
    updateLoading: pushSettingsUpdateLoading,
    currentPushSetting,
    subscriptionVisibility,
    pushSettingVisibility,
    frequencyError,
    countError,
    timeError,
    changePushSettings,
    updatePushSettings,
    resetPushSettings,
    validatePushSettings,
  } = useUserManagementHooks.usePushSettings();

  // 活动设置标签页状态Hook
  const { activeTab, changeActiveTab } = useUserManagementHooks.useActiveSettingTab();

  // 文章设置的hook
  const {
    loading: articleSettingLoading,
    uploading,
    updating: articleSettingUpdating,
    articleSetting,

    changeValue,
    updateArticleSetting,
    uploadArticleSettingImage,
  } = useUserManagementHooks.useArticleSettings();

  // 活动区域状态
  const [activeSection, setActiveSection] = useState('basic-info');
  const contentRef = useRef<HTMLDivElement>(null);
  // 添加菜单项和指示器的DOM引用
  const menuItemsRef = useRef<{ [key: string]: HTMLElement | null }>({});
  const indicatorRef = useRef<HTMLDivElement>(null);
  const prevActiveSection = useRef('basic-info');

  // 更新活动区域时记录前一个活动区域，用于竖线滑动动画
  useEffect(() => {
    prevActiveSection.current = activeSection;
  }, [activeSection]);

  // 计算是否显示订阅设置相关的菜单
  const showSubscriptionSection = useMemo(() => {
    return subscriptionVisibility.length > 0;
  }, [subscriptionVisibility]);

  // 导航菜单项配置 - 添加层级关系标识
  const menuItems = useMemo(() => {
    return [
      { key: 'basic-info', label: t("agent_dashboard.user_detail.menus.base_info"), level: 0, visible: true },
      { key: 'agent-info', label: t("agent_dashboard.user_detail.menus.agent_info"), level: 0, visible: true },
      { key: 'subscription', label: t("agent_dashboard.user_detail.menus.subscription_settings"), level: 0, visible: showSubscriptionSection },
      { key: 'template', label: t("agent_dashboard.user_detail.menus.template"), level: 1, parent: 'subscription', visible: showSubscriptionSection },
      { key: 'material', label: t("agent_dashboard.user_detail.menus.assets"), level: 1, parent: 'subscription', visible: showSubscriptionSection },
      { key: 'knowledge', label: t("agent_dashboard.user_detail.menus.knowledge"), level: 1, parent: 'subscription', visible: showSubscriptionSection },
      { key: 'push', label: t("agent_dashboard.user_detail.menus.push_settings"), level: 1, parent: 'subscription', visible: showSubscriptionSection },
    ]
  }, [subscriptionVisibility]);

  // 菜单点击处理函数 - 实现平滑滚动和延迟激活状态更新
  const handleMenuClick = (key: string) => {
    // 先滚动到目标元素
    const element = document.getElementById(key);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }

    // 延迟更新activeSection，等待竖线动画完成
    // 使用300ms与竖线transition动画时长保持一致
    setTimeout(() => {
      setActiveSection(key);
    }, 200);
  };

  // 滚动监听 - 自动更新激活菜单项
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollPosition = contentRef.current.scrollTop + 100; // 添加偏移量以提前触发
      let closestKey = activeSection;
      let minDistance = Infinity;

      for (const key of menuItems.map(item => item.key)) {
        const element = document.getElementById(key);
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        const sectionTop = rect.top + contentRef.current.scrollTop;

        const distance = Math.abs(scrollPosition - sectionTop);
        if (distance < minDistance) {
          minDistance = distance;
          closestKey = key;
        }
      }

      // 仅当激活项真正变化时才更新，避免多次setState引发卡顿
      if (closestKey !== activeSection) {
        setActiveSection(closestKey);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [menuItems, activeSection]);

  // 更新竖线指示器位置
  useEffect(() => {
    const activeTabElement = menuItemsRef.current[activeSection];
    const indicatorElement = indicatorRef.current;

    if (activeTabElement && indicatorElement) {
      const { top, height } = activeTabElement.getBoundingClientRect();
      const containerTop = activeTabElement.parentElement?.getBoundingClientRect().top || 0;
      const offsetTop = top - containerTop;

      indicatorElement.style.transform = `translateY(${offsetTop}px)`;
      indicatorElement.style.height = `${height}px`;
    }
  }, [activeSection]);

  return (
    <div className='relative w-full h-screen overflow-hidden flex flex-col'>
      <div className='flex-shrink-0 z-10'>
        <UserDetailHeader
          username={userAgentInfo?.name || ''}
          updateLoading={updateLoading}
          updateUser={updateUser}
          updatePushSettings={updatePushSettings}
          updateArticleSetting={updateArticleSetting}
          resetUserDetail={resetUserDetail}
          validatePushSettings={validatePushSettings}
          validateUsers={validateUsers}
          resetPushSettings={resetPushSettings}
          scrollTo={handleMenuClick}
        />
      </div>
      <div className='flex-1 flex px-10 py-4 overflow-hidden'>
        {/* 侧边导航栏 */}
        <div className='w-32 flex-shrink-0 max-h-max'>
          <div className='relative'>
            {/* 固定的竖线指示器 */}
            <div
              ref={indicatorRef}
              className="absolute left-0 w-1 bg-[rgba(16,24,40,1)] transition-all duration-200 ease-in-out"
            />
            {menuItems.map((item) => item.visible && (
              <button
                key={item.key}
                ref={(el) => (menuItemsRef.current[item.key] = el)}
                onClick={() => handleMenuClick(item.key)}
                className={`w-full text-left px-4 py-0.5 mb-3 transition-all duration-300 flex items-center relative group`}
                style={{ paddingLeft: item.level === 1 ? '20px' : '4px' }}
              >
                <span
                  className={`ml-3 transition-colors duration-300 text-sm ${activeSection === item.key ? 'text-[rgba(16,24,40,1)] font-semibold' : 'text-[rgba(113,118,128,1)] font-medium'}`}
                >
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 内容区域 - 带滚动监听 */}
        <div className='flex-1 flex flex-col overflow-y-auto' ref={contentRef}>
          <div className='pl-16 pb-20'>
            {/* 基础信息 */}
            <div id='basic-info' className='pr-8'>
              <BasicInformation
                userBasicInfo={userBasicInfo}
                loading={loading}
                // nameError={nameError}
                genderError={genderError}
                clearUserDetail={clearUserDetail}
                changeValue={changeBasicInfoValue}
              />
            </div>
            {/* 智能体信息 */}
            <div id='agent-info' className='pt-6 pr-8'>
              <PersonalAgentInfo
                loading={loading}
                userAgentInfo={userAgentInfo}
                nameError={userAgentNameError}
                personalTitleError={personalTitleError}
                changeValue={changeAgentInfoValue}
                setInfoTemplateToIntroduction={setInfoTemplateToIntroduction}
              />
            </div>
            {showSubscriptionSection && (
              <>
                {/* 订阅设置 */}
                <div id='subscription' className='pt-6 pr-8 '>
                  <SubscriptionSetting
                    subscribeInfo={subscribeInfo}
                    loading={loading}
                    changeValue={changeSubscribeInfoValue}
                    subscriptionVisibility={subscriptionVisibility}
                  />
                </div>
                {/* tabs */}
                <div className='mt-8 pr-8'>
                  <SettingsTabs
                    activeTab={activeTab}
                    changeActiveTab={changeActiveTab}
                    subscriptionVisibility={subscriptionVisibility}
                  />
                </div>
                <div className='relative'>
                  {/* 模板（视频、图片、文章） */}
                  {['video', 'image', 'article'].includes(activeTab) && (
                    <div id='template' className='mt-5 pr-8'>
                      {activeTab === SETTING_TABS.video && (
                        <TemplateVideoNew
                          actors={agentUserActors}
                          voices={agentUserVoices}
                          activeTab={activeTab}
                        />
                      )}
                      {activeTab === SETTING_TABS.image && (
                        <TemplateImage
                          actors={agentUserActors}
                          voices={agentUserVoices}
                          activeTab={activeTab}
                        />
                      )}
                      {activeTab === SETTING_TABS.article && (
                        <TemplateArticle
                          actors={agentUserActors}
                          voices={agentUserVoices}
                          activeTab={activeTab}
                        />
                      )}
                    </div>
                  )}
                  {/* 声音（播客） */}
                  {['podcast'].includes(activeTab) && (
                    <div id='voices' className='mt-5 pr-8'>
                      <Voice
                        voices={agentUserVoices}
                        loading={loading || podcastLoading}
                        podcastVoices={podcastVoices}
                      />
                    </div>
                  )}
                  {['article'].includes(activeTab) && (
                    <div className='mt-18'>
                      <div id='article-header' className='mt-5 mb-6 pr-8'>
                        <ArticleHeader
                          articleHeaderCopy={articleSetting.articleHeaderCopy}
                          onChange={changeValue}
                        />
                      </div>
                      <div id='article-bottom-poster' className='mt-5 pr-8'>
                        <ArticleBottomPoster
                          articleDrainageImage={articleSetting.articleDrainageImage}
                          uploading={uploading}
                          upload={uploadArticleSettingImage}
                          onChange={changeValue}
                        />
                      </div>
                    </div>
                  )}
                  {/* 素材库（视频、朋友圈、图片、文章） */}
                  {['video', 'moments', 'image', 'article'].includes(activeTab) && (
                    <div id='material' className={cn(
                      "mt-18 pr-8",
                      activeTab === SETTING_TABS.moments && "mt-6",
                      activeTab === SETTING_TABS.article && "mt-6",
                    )}>
                      <MaterialLibrary activeTab={activeTab} />
                    </div>
                  )}
                  {/* 知识库 */}
                  {(
                    <div id='knowledge' className='mt-6 pr-8'>
                      <KnowledgeBase activeTab={activeTab} />
                    </div>
                  )}
                  {/* 推送设置*/}
                  {pushSettingVisibility[PUSH_SETTINGS_TYPE[activeTab]] === 1 && (
                    <div id='push' className='mt-8 pr-8'>
                      <PushSettings
                        currentPushSetting={currentPushSetting}
                        frequencyError={frequencyError}
                        countError={countError}
                        timeError={timeError}
                        changePushSettings={changePushSettings}
                      />
                    </div>
                  )}
                  {/* 禁用状态下的遮罩层 */}
                  {!subscribeInfo[SUBSCRIPTION_MAP[activeTab]] && <div className='absolute inset-0 w-full h-full flex items-center justify-center bg-white/40 z-50'></div>}
                </div>
              </>
            )}
          </div>
          <LoadingOverlay loading={updateLoading || pushSettingsUpdateLoading || articleSettingUpdating} />
        </div>
      </div>
    </div>
  )
}

{/* <div className='px-8 pt-6'>
  <ActorsInformation actors={agentUserActors} voices={agentUserVoices} loading={loading} />
</div> */}
